import React from 'react';
import { AwardIcon, CheckIcon } from 'lucide-react';
import { Role } from '../types/commission';
import { formatCurrency, stepShortLabel } from '../utils/commission';
interface RoleSummaryCardProps {
  role: Role;
  units: number;
  activeStepIndex: number;
}
export function RoleSummaryCard({
  role,
  units,
  activeStepIndex
}: RoleSummaryCardProps) {
  const topStep = role.steps[role.steps.length - 1];
  const structure = role.model === 'bps' ? `${role.baseBps} BPS base · up to ${topStep.totalBps} BPS` : `Up to ${formatCurrency(topStep.perUnit ?? 0)} per ${role.incentiveNoun ?? 'unit'}`;
  return <section aria-labelledby="role-summary-heading" className="flex h-full flex-col overflow-hidden rounded-xl border border-brand-900 bg-brand-900 p-6 shadow-card" data-id="element-118" data-mp="1qm2y0l99556l">
      <div className="flex items-start justify-between gap-4" data-id="element-119" data-mp="y48hq19nm5l0">
        <div data-id="element-120" data-mp="t5x5xkzstl05">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-200" data-id="element-121" data-mp="t66z1hg548a2">Current role</p>
          <h2 id="role-summary-heading" className="mt-1.5 text-2xl font-bold text-white" data-id="element-122" data-mp="2ywas7abhjfre">
            {role.name}
          </h2>
        </div>
        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25" data-id="element-123" data-mp="33taxhj0igehg">
          <AwardIcon className="h-5 w-5 text-white" data-id="element-124" data-mp="3djne5057fnib" />
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-id="element-125" data-mp="2fxgaptkn017j">
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-brand-100" data-id="element-126" data-mp="2fyrcjnuiuyw0">
          {role.scope} production
        </span>
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-brand-100" data-id="element-127" data-mp="2pukz801yg8ri">{structure}</span>
      </div>

      <div className="mt-5" data-id="element-128" data-mp="2txhb77wzp6mh">
        <div className="flex gap-1.5" aria-hidden="true" data-id="element-129" data-mp="2tulgdsgfuy29">
          {role.steps.map((step, index) => <span key={step.minUnits} className={`h-1.5 flex-1 rounded-full ${index <= activeStepIndex ? 'bg-gold-400' : 'bg-white/20'}`} data-id="element-130" data-mp="577zlsfmkj86" data-mpi={index} />)}
        </div>
        <p className="mt-2 text-xs font-medium text-brand-200" data-id="element-131" data-mp="2180cz603l9af">
          {units} {role.unitNounPlural} · schedule band {stepShortLabel(role.steps[activeStepIndex])}
        </p>
      </div>

      {role.monthlyCap ? <p className="mt-4 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-brand-100" data-id="element-132" data-mp="2a86mkgsodp4b">
          Monthly commission capped at {formatCurrency(role.monthlyCap)}
        </p> : null}

      {role.promotion ? <div className="mt-auto space-y-4 pt-6" data-id="element-133" data-mp="1iik1xoqsdyfr">
          <p className="text-sm font-semibold text-white" data-id="element-134" data-mp="1w8x5zg0k4szt">
            Next: {role.promotion.toRoleName}
            {role.promotion.requirements.length > 1 ? <span className="ml-2 text-xs font-medium uppercase tracking-wide text-brand-300" data-id="element-135" data-mp="1rgnmahq1cp5z">
                {role.promotion.logic === 'or' ? 'Either path' : 'Both required'}
              </span> : null}
          </p>
          {role.promotion.requirements.map(requirement => <RequirementRow key={requirement.label} label={requirement.label} valueText={`${requirement.value}/${requirement.goal} ${requirement.suffix}`} pct={Math.min(100, Math.round(requirement.value / requirement.goal * 100))} met={requirement.value >= requirement.goal} data-id="element-136" data-mp="3afbr3ab3r7o1" />)}
        </div> : <p className="mt-auto pt-6 text-sm font-medium text-brand-200" data-id="element-137" data-mp="3u5dqb0r67zw2">
          Paid on {role.scope.toLowerCase()} production — no further tier above this plan.
        </p>}
    </section>;
}
interface RequirementRowProps {
  label: string;
  valueText: string;
  pct: number;
  met: boolean;
}
function RequirementRow({
  label,
  valueText,
  pct,
  met
}: RequirementRowProps) {
  return <div data-id="element-138" data-mp="28py6i11vw9s0">
      <div className="flex items-center justify-between gap-3 text-sm" data-id="element-139" data-mp="28q7x9qur6kyr">
        <span className="flex items-center gap-2 text-brand-100" data-id="element-140" data-mp="2drnhjgp3wng3">
          {met ? <CheckIcon className="h-4 w-4 shrink-0 text-gold-400" aria-hidden="true" data-id="element-141" data-mp="2t0iuos2q50sp" /> : <span aria-hidden="true" className="h-4 w-4 shrink-0 rounded-full border border-brand-300" data-id="element-142" data-mp="2o9aos60lrxka" />}
          {label}
        </span>
        <span className="shrink-0 font-semibold tabular-nums text-white" data-id="element-143" data-mp="2k5uo8ylexd2b">{valueText}</span>
      </div>
      <div role="progressbar" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/15" data-id="element-144" data-mp="2f71bx9hv3s3q">
        <div className={`h-full rounded-full ${met ? 'bg-gold-400' : 'bg-brand-400'}`} style={{
        width: `${pct}%`
      }} data-id="element-145" data-mp="32xx9kn901hiw" />
      </div>
    </div>;
}